import React from 'react';
import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import axios from 'axios';

import { logger } from 'packages';
import { handleError, API_ERRORS } from 'tradera-utils/api';
import itemActions from 'tradera-state/items/actions';
import uiActions from 'tradera-state/ui/actions';
import RecommendationsView from './recommendations-view';
import { withUrlLocalizer } from 'tradera-lang';

const loadingStates = {
    // can transition to is represented by '->'
    initializing: 'initializing', // -> loading
    loading: 'loading', // -> ready, end, error
    ready: 'ready', // -> loading
    end: 'end', // final state
    error: 'error' // final state
};

class Recommendations extends React.Component {
    static propTypes = {
        createRequestUrl: PropTypes.func.isRequired,
        collapseIfEmpty: PropTypes.bool,
        enableQuickView: PropTypes.bool,
        infiniteScrollEnabled: PropTypes.bool,
        itm: PropTypes.object,
        onEnteringErrorState: PropTypes.func,
        onReachedEnd: PropTypes.func,
        openQuickView: PropTypes.func,
        ErrorMessage: PropTypes.node,
        LoadingSymbol: PropTypes.node.isRequired,
        NoMoreItemsMessage: PropTypes.node,
        Title: PropTypes.node,
        toLocalizedUrl: PropTypes.func
    };

    constructor(props) {
        super(props);
        this.state = {
            loadingState: loadingStates.initializing,
            currentPage: 1,
            // Used to keep sorting of items
            itemIds: []
        };
    }

    getItems = async () => {
        const { currentPage } = this.state;
        const { createRequestUrl, toLocalizedUrl } = this.props;

        this.setState({
            loadingState: loadingStates.loading
        });

        const endpoint = toLocalizedUrl(createRequestUrl(currentPage));

        return this.fetchItems(endpoint)
            .then(this.onRequestSuccess)
            .catch(this.onRequestError);
    };

    fetchItems(endpoint) {
        return axios.get(endpoint).catch(handleError());
    }

    onRequestSuccess = success => {
        const { onReachedEnd, onEnteringErrorState } = this.props;
        const { itemIds } = this.state;
        const itemsInResponse = success.data.items.length;

        if (itemsInResponse < 1 && itemIds.length === 0) {
            this.setState({ loadingState: loadingStates.error });
            if (onEnteringErrorState) {
                onEnteringErrorState();
            }
        } else if (itemsInResponse < 1 && itemIds.length > 0) {
            this.setState({ loadingState: loadingStates.end });
            if (onReachedEnd) {
                onReachedEnd(itemIds);
            }
        } else {
            this.props.addItems(success.data.items);
            this.setState(prevState => ({
                loadingState: loadingStates.ready,
                itemIds: [
                    ...prevState.itemIds,
                    ...success.data.items.map(x => x.itemId)
                ]
            }));
        }
    };

    onRequestError = error => {
        const { itemIds } = this.state;
        const { onEnteringErrorState } = this.props;

        if (itemIds.length === 0) {
            this.setState({ loadingState: loadingStates.error });
            if (onEnteringErrorState) {
                onEnteringErrorState();
            }
        } else if (itemIds.length > 0) {
            this.setState({ loadingState: loadingStates.end });
        }

        switch (error.message) {
            case API_ERRORS.ABORTED:
            case API_ERRORS.CANCELLED:
            case API_ERRORS.NETWORK:
                break;
            default:
                logger(error);
        }
    };

    handleQuickViewClick = itemId => {
        const { openQuickView, gtmLabel } = this.props;
        const { itemIds } = this.state;
        this.setState({
            quickViewOpen: itemId || false
        });
        openQuickView(itemId, itemIds, gtmLabel);
    };

    setLoadingTriggerRef = ref => {
        this.loadingTriggerRef = ref;
    };

    intersectionObserved = entities => {
        const entry = entities[0];
        const { isIntersecting } = entry;

        if (!isIntersecting || this.state === loadingStates.loading) {
            return;
        }

        this.setState(prevState => ({
            currentPage: prevState.currentPage + 1
        }));

        this.getItems();
    };

    componentDidMount() {
        const { infiniteScrollEnabled } = this.props;
        this.getItems();

        if (infiniteScrollEnabled === true) {
            const observerOptions = {
                root: null, // Page as root
                rootMargin: '0px 0px 800px 0px', // Increase to load earlier
                threshold: 1.0
            };

            if (!this.observer) {
                this.observer = new IntersectionObserver(
                    this.intersectionObserved,
                    observerOptions
                );
            }

            this.observer.observe(this.loadingTriggerRef);
        }
    }

    render() {
        const { loadingState, itemIds } = this.state;
        const {
            collapseIfEmpty,
            children,
            enableQuickView,
            infiniteScrollEnabled,
            items,
            itm,
            isLoggedIn,
            preferredCurrency,
            toggleWishlist
        } = this.props;
        const {
            ErrorMessage,
            LoadingSymbol,
            NoMoreItemsMessage,
            Title
        } = this.props;

        if (collapseIfEmpty && loadingState === loadingStates.error) {
            return null; // collapse
        }

        return (
            <RecommendationsView
                collapseIfEmpty={collapseIfEmpty}
                ErrorMessage={ErrorMessage}
                infiniteScrollEnabled={infiniteScrollEnabled}
                isLoggedIn={isLoggedIn}
                itemIds={itemIds}
                items={items}
                itm={itm}
                loadingState={loadingState}
                LoadingSymbol={LoadingSymbol}
                NoMoreItemsMessage={NoMoreItemsMessage}
                onQuickView={enableQuickView && this.handleQuickViewClick}
                preferredCurrency={preferredCurrency}
                setLoadingTriggerRef={this.setLoadingTriggerRef}
                Title={Title}
                toggleWishlist={toggleWishlist}>
                {children}
            </RecommendationsView>
        );
    }
}

Recommendations.defaultProps = {
    infiniteScrollEnabled: false,
    collapseIfEmpty: true,
    enableQuickView: false
};

const mapStateToProps = state => ({
    items: state.items.cache,
    isLoggedIn: state.member.isLoggedIn,
    preferredCurrency: state.multiCurrency.preferredCurrency,
    enableQuickView: state.environment.featureSwitches['quick-view']
});

const mapDispatchToProps = dispatch => ({
    addItems: items => dispatch(itemActions.add(items)),
    openQuickView: (itemId, itemIds, openedFrom) =>
        dispatch(uiActions.openQuickView({ itemIds, itemId, openedFrom })),
    toggleWishlist: itemId => dispatch(itemActions.wishlist(itemId))
});

export { Recommendations, loadingStates };
export default hot(module)(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(withUrlLocalizer(Recommendations))
);
